import { getOriginalUrl } from '@/helpers/documentHelpers';
import { trimFileNames } from '@/helpers/trimFileNames';
import { useGetDocumentsQuery } from '@/services/documents';
import { useGetRequirementFlagSettingsQuery } from '@/services/requirementFlagSettings';
import { Box, Icon } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { DocumentDocument, FlagDocumentTypes, FlagPurposeType, RequirementFlagSettingsDocument } from 'types';

import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';

import { FlagProps } from './Flag';
import { QueueFlagsContext } from './QueueFlagsContext';

interface FlagBoxProps extends FlagProps {
  dataKeys?: string[];
  displayWithNoQueue?: boolean;
  isLast?: boolean;
  directFlags?: any[];
}

function FlagNote({ note }: { note?: string }) {
  return note ? (
    <Box fontWeight="light">
      <span style={{ whiteSpace: 'pre-wrap' }}>{`Note: ${note}`}</span>
    </Box>
  ) : null;
}

function FlagNoteUpload({ uploadedDocument, dataKey }: { uploadedDocument?: DocumentDocument; dataKey?: string }) {
  return uploadedDocument?.files?.length ? (
    <Box fontWeight="light" direction="column" gap="xs">
      {uploadedDocument?.files?.map((file, index) => (
        <Box key={index} direction="row" gap="xs" alignItems="center">
          <Icon name="paperclip" color="body-secondary" aria-label="attachment" size="sm" />
          <a
            target="_blank"
            rel="noreferrer noopener"
            href={getOriginalUrl(file?.viewUrls)}
            title={file.originalName}
          >
            {trimFileNames(file.originalName, dataKey)}
          </a>
        </Box>
      ))}
    </Box>
  ) : null;
}

function FlagAttachedDocuments({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <Box fontWeight="light" direction="row" gap="xs" alignItems="center">
      <Icon name="paperclip" color="body-secondary" aria-label="attachment" size="sm" />
      <span>
        {count} attached package document{count === 1 ? '' : 's'}
      </span>
    </Box>
  );
}

const EMPTY_FLAGS: any[] = [];

export const FlagInfo = ({
  dataKey,
  dataKeys,
  displayWithNoQueue = false,
  fileName,
  flagType,
  directFlags = EMPTY_FLAGS,
}: FlagBoxProps) => {
  const { flagDrivenPackageReview } = useFlags();
  const { claims } = useAuth();
  const canEditFlag = isPalmettoFinanceUser(claims);
  const navigate = useNavigate();
  const location = useLocation();
  const milestonePackageURI = location.pathname.split('/')[3];
  const { data: requirementFlagSettings } = useGetRequirementFlagSettingsQuery(
    { type: flagType },
    { skip: !flagType },
  );
  const { id = '' } = useParams<{ id: string }>();
  const { getFlag, getFlagsByFlagType, shouldHide, showEditModal, showResolveModal, getVisibleFlags } =
    useContext(QueueFlagsContext);
  const dataKeysForGetFlags = dataKeys && dataKeys?.length > 1 ? dataKeys : dataKey;
  const currentFlag = getFlag(dataKeysForGetFlags, fileName, false);

  const currentFlags = useMemo(
    () => (flagType ? getFlagsByFlagType(flagType, false, dataKeysForGetFlags, fileName) || [] : []),
    [flagType, getFlagsByFlagType, dataKeysForGetFlags, fileName],
  );

  const flagsOnDisplay = useMemo(() => {
    const combinedFlags = [...(currentFlags ?? []), ...(currentFlag ? [currentFlag] : []), ...directFlags];
    const filtered = getVisibleFlags(combinedFlags);
    return Array.from(new Set(filtered.map((f) => f.id)))
      .map((flagId) => filtered.find((f) => f.id === flagId)!)
      .filter(Boolean);
  }, [currentFlag, currentFlags, getVisibleFlags, directFlags]);

  const hasFlags = flagDrivenPackageReview ? Boolean(flagsOnDisplay?.length) : Boolean(currentFlag);
  const uploadedFlagDocuments = useGetDocumentsQuery({
    accountId: id,
    documentTypeFilter: [FlagDocumentTypes.flag],
  });
  const setting: RequirementFlagSettingsDocument | undefined = requirementFlagSettings?.requirementFlagSettings?.[0];

  const flagPurposeColor: Record<FlagPurposeType, 'danger-500' | 'warning-600' | 'grey-500' | 'info-500'> = {
    [FlagPurposeType.rejection]: 'danger-500',
    [FlagPurposeType.stipulation]: 'warning-600',
    [FlagPurposeType.additionalReview]: 'grey-500',
    [FlagPurposeType.notification]: 'info-500',
    [FlagPurposeType.changeOrder]: 'info-500',
  };

  const handleFlagClick = (accountId: string, flagId: string) => {
    navigate(`/accounts/${accountId}/${milestonePackageURI}/flags/${flagId}`);
  };

  return hasFlags || !shouldHide || displayWithNoQueue
    ? flagsOnDisplay?.map((flag, i) => {
        const uploadedDocument = uploadedFlagDocuments?.data?.find(
          (d: DocumentDocument) => d.id === flag?.fileUpload,
        );
        const displayReason =
          flag?.reason ||
          (flag?.problems?.length &&
            setting?.acceptanceCriteria?.find((criterion) => {
              const criterionId = flag?.problems && flag?.problems[0]?.acceptanceCriteriaId.toString();
              return criterion?.id?.toString() === criterionId;
            })?.displayText) ||
          dataKey;
        const flagIsEditable = flag?.id && flag?.flagType;
        return flagDrivenPackageReview ? (
          <Box
            key={i}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="100"
            padding={i === flagsOnDisplay.length - 1 ? '0' : '0 0 md'}
          >
            <Box direction="row" alignItems="center" gap="sm">
              {canEditFlag && (
                <Box
                  onClick={() =>
                    flagIsEditable
                      ? showEditModal(flag)
                      : showResolveModal(flag?.dataKey ?? '', fileName, flag?.id?.toString())
                  }
                  cursor="pointer"
                  padding="0"
                  borderColor="transparent"
                  borderWidth="0"
                >
                  <Icon size="lg" name={flagIsEditable ? 'pencil' : 'c-remove'} color="body-primary" />
                </Box>
              )}
              <Box>
                <Box fontWeight="bold" color={flag?.purposeType ? flagPurposeColor[flag?.purposeType] : 'danger'}>
                  {displayReason}
                </Box>
                {!flagIsEditable && (
                  <Box>
                    <FlagNote note={flag?.note} />
                    <FlagNoteUpload uploadedDocument={uploadedDocument} dataKey={dataKey} />
                    <FlagAttachedDocuments count={flag?.attachedDocumentIds?.length ?? 0} />
                  </Box>
                )}
              </Box>
            </Box>
            {flagIsEditable ? (
              <Box
                onClick={() => {
                  if (flag.id) {
                    handleFlagClick(id, flag.id.toString());
                  }
                }}
                cursor="pointer"
                padding="0"
                borderColor="transparent"
                borderWidth="0"
              >
                <Icon size="md" name="caret-right" color="body-primary" />
              </Box>
            ) : (
              <Box as="i" width="100" textAlign="right">
                {canEditFlag ? 'Resolve-only' : 'No further details'}
              </Box>
            )}
          </Box>
        ) : (
          <Box key={i}>
            {flag?.reason && <Box fontWeight="medium">{flag.reason}</Box>}
            {flag?.problems?.length && (
              <Box fontWeight="light" key={i}>
                {setting?.acceptanceCriteria?.find((criterion) => {
                  const criterionId = flag?.problems && flag?.problems[0]?.acceptanceCriteriaId.toString();
                  return criterion?.id?.toString() === criterionId;
                })?.displayText || flag?.reason}
              </Box>
            )}
            <FlagNote note={flag?.note} />
            <FlagNoteUpload uploadedDocument={uploadedDocument} dataKey={dataKey} />
            <FlagAttachedDocuments count={flag?.attachedDocumentIds?.length ?? 0} />
          </Box>
        );
      })
    : null;
};
